<div class="import-card">
  <div class="card-header">
    <div class="card-header-text">
      <h2 class="card-title">Import customer data</h2>
      <p class="card-description">
        Migrate customers and vehicles into this car wash from a CSV, a Google Sheet, or directly
        from a connected vendor. Jobs run in the background and apply to the entire car wash.
      </p>
    </div>
    <span
      class="header-status"
      [class.running]="isJobRunning"
      [class.success]="jobStatus === 'completed'"
      [class.danger]="jobStatus === 'failed'"
    >
      {{ activeJobId ? statusLabel : 'Ready' }}
    </span>
  </div>

  <div class="import-body">
    <section class="section" aria-labelledby="import-source-heading">
      <div class="section-heading">
        <h3 id="import-source-heading" class="section-title">1. Choose source</h3>
        <p class="section-subtitle">Select how you want to provide the migration file.</p>
      </div>

      <div class="source-select-row">
        <label class="field-label" for="import-source">Source</label>
        <select
          id="import-source"
          class="form-input source-select"
          name="importSource"
          [ngModel]="sourceMode"
          (ngModelChange)="selectSourceMode($event)"
          [disabled]="isSubmitting || isJobRunning"
        >
          @for (option of sourceOptions; track option.value) {
            <option [value]="option.value">{{ option.label }}</option>
          }
        </select>
        <div class="field-hint">{{ selectedSourceHint }}</div>
      </div>
    </section>

    <section class="section" aria-labelledby="import-details-heading">
      <div class="section-heading">
        <h3 id="import-details-heading" class="section-title">2. Provide data</h3>
        <p class="section-subtitle">
          @if (sourceMode === 'csv') {
            Upload a CSV that matches the migration template.
          } @else if (isVendorMode) {
            Connect this car wash to {{ activeVendorLabel }}, then pull the member snapshot.
          } @else {
            Paste the spreadsheet link and optional tab name.
          }
        </p>
      </div>

      <div class="details-layout">
        <div class="details-main">
          @if (sourceMode === 'csv') {
            @if (!selectedFile) {
              <div
                class="upload-dropzone"
                [class.drag-over]="isDragOver"
                (dragover)="onDragOver($event)"
                (dragleave)="onDragLeave($event)"
                (drop)="onDrop($event)"
                (click)="fileInput.click()"
                role="button"
                tabindex="0"
                (keydown.enter)="fileInput.click()"
                (keydown.space)="$event.preventDefault(); fileInput.click()"
              >
                <div class="upload-icon-container" aria-hidden="true">
                  <img
                    src="/assets/images/upload-icon.svg"
                    alt=""
                    class="upload-icon"
                    width="22"
                    height="22"
                  />
                </div>
                <div class="upload-text">
                  <p class="upload-title">
                    Drop your CSV here, or <span class="click-to-upload">browse files</span>
                  </p>
                  <p class="upload-subtitle">CSV only · max 25MB</p>
                </div>
                <input
                  #fileInput
                  type="file"
                  accept=".csv,text/csv"
                  (change)="onFileSelected($event)"
                  hidden
                />
              </div>
            } @else {
              <div class="selected-file-display">
                <div class="file-icon" aria-hidden="true">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8l-5-5Z"
                      stroke="currentColor"
                      stroke-width="1.5"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M14 3v5h5"
                      stroke="currentColor"
                      stroke-width="1.5"
                      stroke-linejoin="round"
                    />
                  </svg>
                </div>
                <div class="file-info">
                  <span class="file-name">{{ selectedFile.name }}</span>
                  <span class="file-size">{{ formatFileSize(selectedFile.size) }}</span>
                </div>
                <button
                  type="button"
                  class="remove-file-btn"
                  (click)="onRemoveFile()"
                  [disabled]="isSubmitting || isJobRunning"
                >
                  Remove
                </button>
              </div>
            }
          }

          @if (sourceMode === 'google_sheet') {
            <div class="sheet-fields">
              <div class="form-field">
                <label class="field-label" for="google-sheet-url">Google Sheet URL or ID</label>
                <input
                  id="google-sheet-url"
                  type="text"
                  class="form-input"
                  name="googleSheetUrl"
                  [(ngModel)]="googleSheetUrl"
                  placeholder="https://docs.google.com/spreadsheets/d/…"
                  [disabled]="isSubmitting || isJobRunning"
                  autocomplete="off"
                />
                <div class="field-hint">
                  Share the sheet as Viewer with this environment’s Google service account.
                </div>
              </div>
            </div>
          }

          @if (isVendorMode) {
            <div class="vendor-fields">
              <!-- Connection state, so it is obvious whether a sync can run -->
              <div class="connection-row" aria-live="polite">
                @if (credentialStatusLoading) {
                  <span class="connection-pill loading">Checking connection…</span>
                } @else if (credentialStatus?.configured) {
                  <span class="connection-pill connected">
                    Connected
                    @if (credentialStatus?.locationCode) {
                      · {{ credentialStatus?.locationCode }}
                    }
                  </span>
                } @else if (!credentialStatus && credentialError) {
                  <!-- Status could not be read at all — do not claim "not
                       connected", which implies we checked successfully. -->
                  <span class="connection-pill disconnected">Status unavailable</span>
                } @else {
                  <span class="connection-pill disconnected">Not connected</span>
                }
                <button
                  type="button"
                  class="link-btn"
                  (click)="loadCredentialStatus()"
                  [disabled]="credentialStatusLoading"
                >
                  Refresh
                </button>
              </div>

              @if (credentialStatus && !credentialStatus.encryptionConfigured) {
                <div class="alert alert-warning" role="status">
                  Credential encryption is not configured on this environment
                  (<code>VENDOR_SYNC_ENCRYPTION_KEY</code>), so credentials cannot be saved. You can
                  still verify a login below.
                </div>
              }

              @if (credentialStatus?.unavailableReason) {
                <div class="alert alert-warning" role="status">
                  Credential storage is unavailable: {{ credentialStatus?.unavailableReason }}
                </div>
              }

              <!-- Pause/resume. Non-destructive, unlike Remove below: credentials and the
                   plan mapping survive, so resuming needs no re-setup. -->
              @if (credentialStatus?.configured) {
                <div class="sync-toggle-row">
                  <div class="sync-toggle-text">
                    <span class="sync-toggle-title">
                      {{ credentialStatus?.enabled ? 'Sync is on' : 'Sync is off' }}
                    </span>
                    <span class="sync-toggle-hint">
                      @if (credentialStatus?.enabled) {
                        Runs nightly and can be started manually.
                      } @else {
                        Not syncing, and vendor tags are hidden. Already-imported customers and
                        their memberships are unaffected.
                      }
                    </span>
                  </div>
                  <button
                    type="button"
                    class="secondary-btn"
                    (click)="onToggleSyncEnabled()"
                    [disabled]="isTogglingSyncEnabled || isJobRunning"
                  >
                    @if (isTogglingSyncEnabled) {
                      Saving…
                    } @else {
                      {{ credentialStatus?.enabled ? 'Turn off' : 'Turn on' }}
                    }
                  </button>
                </div>
              }

              <!-- Lane redeem controls (CWS-920). Deliberately below the sync toggle
                   and above the credential summary: these govern the gate, not the
                   nightly pull, and stay in force while the sync is paused. -->
              @if (credentialStatus?.configured) {
                <div class="redeem-controls">
                  <div class="redeem-controls-head">
                    <span class="redeem-controls-title">Membership redeem at the lane</span>
                    <span class="redeem-controls-hint">
                      Applies to already-imported members. Independent of the sync switch above.
                    </span>
                  </div>

                  <div class="sync-toggle-row">
                    <div class="sync-toggle-text">
                      <span class="sync-toggle-title">
                        {{
                          plateMatching.collectPayment
                            ? 'Collect payment is shown'
                            : 'Collect payment is hidden'
                        }}
                      </span>
                      <span class="sync-toggle-hint">
                        @if (plateMatching.collectPayment) {
                          Staff see Collect payment / card-on-file on the Active subscription modal
                          for imported members who do not yet have a Way card.
                        } @else {
                          The Active subscription modal does not ask for a card. Barcode and plate
                          redeem still work. Turn this on if you want staff to capture a card for
                          later billing.
                        }
                      </span>
                    </div>
                    <button
                      type="button"
                      class="secondary-btn"
                      (click)="onToggleVendorCollectPayment()"
                      [disabled]="isSavingPlateMatching"
                    >
                      @if (isSavingPlateMatching) {
                        Saving…
                      } @else {
                        {{ plateMatching.collectPayment ? 'Turn off' : 'Turn on' }}
                      }
                    </button>
                  </div>

                  <div class="sync-toggle-row">
                    <div class="sync-toggle-text">
                      <span class="sync-toggle-title">
                        {{
                          plateMatching.autoRedeem
                            ? 'Plate reads redeem automatically'
                            : 'Plate reads ask an attendant'
                        }}
                      </span>
                      <span class="sync-toggle-hint">
                        @if (plateMatching.autoRedeem) {
                          A camera plate match redeems the membership and completes the visit with
                          no prompt.
                        } @else {
                          A camera plate match opens an order request instead, so an attendant
                          confirms. Staff can still redeem by scanning the member's barcode. This is
                          the default — turn it on once you trust the plate data for this car wash.
                        }
                      </span>
                    </div>
                    <button
                      type="button"
                      class="secondary-btn"
                      (click)="onToggleVendorAutoRedeem()"
                      [disabled]="isSavingPlateMatching"
                    >
                      @if (isSavingPlateMatching) {
                        Saving…
                      } @else {
                        {{ plateMatching.autoRedeem ? 'Turn off' : 'Turn on' }}
                      }
                    </button>
                  </div>

                  <div class="redeem-locations">
                    <div class="redeem-locations-head">
                      <span class="sync-toggle-title">
                        @if (isPlateMatchingLocationRestricted) {
                          Allowed at {{ plateMatching.locationIds.length }} of
                          {{ shopLocations.length }} locations
                        } @else {
                          Allowed at every location
                        }
                      </span>
                      <span class="sync-toggle-hint">
                        <!-- Stated plainly: this is the control that stops a member's
                             plate matching at a site that never carried their membership. -->
                        Tick only the locations that migrated from {{ activeVendorLabel }}. Outside
                        these, its memberships will not redeem at all — camera or barcode. Leave all
                        unticked to allow every location.
                      </span>
                    </div>

                    @if (shopLocationsLoading) {
                      <p class="redeem-locations-empty">Loading locations…</p>
                    } @else if (shopLocations.length === 0) {
                      <p class="redeem-locations-empty">
                        No active locations found for this car wash.
                      </p>
                    } @else {
                      <ul class="redeem-location-list">
                        @for (loc of shopLocations; track loc.id) {
                          <li class="redeem-location-item">
                            <label class="redeem-location-label">
                              <input
                                type="checkbox"
                                [checked]="isPlateMatchingLocationSelected(loc.id)"
                                [disabled]="isSavingPlateMatching"
                                (change)="onTogglePlateMatchingLocation(loc.id)"
                              />
                              <span>{{ loc.branch || loc.name || loc.id }}</span>
                            </label>
                          </li>
                        }
                      </ul>
                      @if (isPlateMatchingLocationRestricted) {
                        <button
                          type="button"
                          class="link-btn"
                          (click)="onClearPlateMatchingLocations()"
                          [disabled]="isSavingPlateMatching"
                        >
                          Allow every location
                        </button>
                      }
                    }
                  </div>

                  @if (plateMatchingError) {
                    <div class="alert alert-warning" role="alert">{{ plateMatchingError }}</div>
                  }
                </div>
              }

              @if (credentialStatus?.configured && !showCredentialForm) {
                <div class="credential-summary">
                  <div class="credential-summary-text">
                    <span class="credential-summary-title">
                      {{ activeVendorLabel }} is connected
                    </span>
                    <span class="credential-summary-hint">
                      Stored credentials are used automatically — you can start a sync without
                      re-entering them.
                      @if (credentialStatus?.lastVerifiedAt) {
                        Last verified {{ formatHistoryDate(credentialStatus?.lastVerifiedAt) }}.
                      }
                    </span>
                  </div>
                  <div class="credential-summary-actions">
                    <button
                      type="button"
                      class="secondary-btn"
                      (click)="onToggleCredentialForm()"
                      [disabled]="isSubmitting || isJobRunning"
                    >
                      Change credentials
                    </button>
                    <button
                      type="button"
                      class="link-btn danger"
                      (click)="onDeleteCredentials()"
                      [disabled]="isSavingCredentials || isSubmitting || isJobRunning"
                    >
                      {{ isSavingCredentials ? 'Removing…' : 'Remove' }}
                    </button>
                  </div>
                </div>
              }

              @if (showCredentialForm) {
                <fieldset class="credential-fieldset">
                  <legend class="field-label">{{ activeVendorLabel }} sign-in</legend>
                  <p class="field-hint">
                    The same credentials used to sign in to {{ activeVendorLabel }}. Stored per car
                    wash, encrypted, and used only by the scheduled sync.
                  </p>

                  <!--
                  Rendered from the adapter's own credentialFields descriptor, so
                  a vendor with a different login shape needs no template change.
                -->
                  <div class="field-grid">
                    @for (field of activeCredentialFields; track field.key) {
                      <div class="form-field">
                        <label class="field-label" [for]="'vendor-cred-' + field.key">
                          {{ field.label }}
                        </label>
                        <input
                          [id]="'vendor-cred-' + field.key"
                          [type]="field.type"
                          class="form-input"
                          [name]="'vendorCred_' + field.key"
                          [(ngModel)]="credentialValues[field.key]"
                          [disabled]="isSubmitting || isJobRunning || !canEditCredentials"
                          [autocomplete]="field.type === 'password' ? 'new-password' : 'off'"
                        />
                        @if (field.type === 'password') {
                          <div class="field-hint">Never displayed again after saving.</div>
                        } @else if (field.hint) {
                          <div class="field-hint">{{ field.hint }}</div>
                        }
                      </div>
                    }
                  </div>

                  <div class="credential-actions">
                    <button
                      type="button"
                      class="secondary-btn"
                      (click)="onTestCredentials()"
                      [disabled]="
                        !hasCredentialInput || isTestingCredentials || isSavingCredentials
                      "
                    >
                      {{ isTestingCredentials ? 'Verifying…' : 'Test connection' }}
                    </button>
                    @if (canEditCredentials) {
                      <button
                        type="button"
                        class="secondary-btn"
                        (click)="onSaveCredentials()"
                        [disabled]="
                          !hasCredentialInput || isSavingCredentials || isTestingCredentials
                        "
                      >
                        {{ isSavingCredentials ? 'Saving…' : 'Save credentials' }}
                      </button>
                    }
                  </div>

                  @if (credentialTestResult?.ok) {
                    <div class="alert alert-success" role="status">
                      Verified.
                      @if (credentialTestResult?.location) {
                        Location <strong>{{ credentialTestResult?.location }}</strong
                        >.
                      }
                      @if (credentialTestResult?.expiresAt) {
                        Session valid until
                        {{ formatHistoryDate(credentialTestResult?.expiresAt) }}.
                      }
                    </div>
                  }

                  @if (credentialError) {
                    <div class="alert alert-error" role="alert">{{ credentialError }}</div>
                  }
                </fieldset>

                @if (credentialStatus?.configured) {
                  <button
                    type="button"
                    class="link-btn"
                    (click)="onToggleCredentialForm()"
                    [disabled]="isSavingCredentials || isTestingCredentials"
                  >
                    Cancel — keep the stored credentials
                  </button>
                }
              }

              <!-- Plan → package mapping (CWS-734 Phase 2). Always visible once a
                   vendor is picked, independent of whether the credential form
                   above is expanded — it's persistent per-shop config, not a
                   momentary editing state. -->
              <fieldset class="credential-fieldset plan-mapping-fieldset">
                <legend class="field-label">Plan → package mapping</legend>
                <p class="field-hint">
                  Map each {{ activeVendorLabel }} plan name to an active monthly membership
                  package. A migrated member on an unmapped (or invalid) plan is still imported as a
                  customer, but is skipped for gate entitlement until it's mapped.
                </p>

                @if (planMappingLoading) {
                  <p class="field-hint">Loading plan mapping…</p>
                } @else {
                  @if (!planMapping?.has_credentials) {
                    <div class="alert alert-warning" role="status">
                      Save {{ activeVendorLabel }} credentials above before a mapping can be saved.
                      You can still build it now — it just won't save until then.
                    </div>
                  }

                  @if (planMappingRows.length) {
                    <div class="plan-mapping-rows">
                      @for (row of planMappingRows; track row; let i = $index) {
                        <div class="plan-mapping-row">
                          <span class="plan-mapping-name">{{ row.planName }}</span>
                          <select
                            class="form-input"
                            [(ngModel)]="row.packageId"
                            (ngModelChange)="onPlanMappingRowPackageChange(row)"
                            name="planMappingPackage{{ i }}"
                            [disabled]="isSavingPlanMapping"
                          >
                            <option value="" disabled>Select a package</option>
                            @for (pkg of planMapping?.available_packages; track pkg.id) {
                              <option [value]="pkg.id">{{ pkg.package_name }}</option>
                            }
                          </select>
                          @if (!row.valid) {
                            <span
                              class="plan-mapping-invalid"
                              [title]="describePlanMappingReason(row.reason)"
                            >
                              Invalid
                            </span>
                          }
                          <button
                            type="button"
                            class="link-btn danger"
                            (click)="onRemovePlanMappingRow(i)"
                            [disabled]="isSavingPlanMapping"
                          >
                            Remove
                          </button>
                        </div>
                      }
                    </div>
                  } @else {
                    <p class="field-hint">No plans mapped yet.</p>
                  }

                  <div class="plan-mapping-add-row">
                    <input
                      type="text"
                      class="form-input"
                      name="newPlanName"
                      placeholder="Plan name, e.g. Unlimited Champion"
                      [(ngModel)]="newPlanName"
                      [disabled]="isSavingPlanMapping"
                      autocomplete="off"
                    />
                    <select
                      class="form-input"
                      name="newPackageId"
                      [(ngModel)]="newPackageId"
                      [disabled]="isSavingPlanMapping"
                    >
                      <option value="" disabled selected>Select a package</option>
                      @for (pkg of planMapping?.available_packages; track pkg.id) {
                        <option [value]="pkg.id">{{ pkg.package_name }}</option>
                      }
                    </select>
                    <button
                      type="button"
                      class="secondary-btn"
                      (click)="onAddPlanMappingRow()"
                      [disabled]="!newPlanName.trim() || !newPackageId || isSavingPlanMapping"
                    >
                      Add plan
                    </button>
                  </div>

                  <div class="credential-actions">
                    <button
                      type="button"
                      class="secondary-btn"
                      (click)="onSavePlanMapping()"
                      [disabled]="!planMapping?.has_credentials || isSavingPlanMapping"
                    >
                      {{ isSavingPlanMapping ? 'Saving…' : 'Save plan mapping' }}
                    </button>
                  </div>

                  @if (planMappingSaved) {
                    <div class="alert alert-success" role="status">Plan mapping saved.</div>
                  }
                  @if (planMappingError) {
                    <div class="alert alert-error" role="alert">{{ planMappingError }}</div>
                  }
                }
              </fieldset>
            </div>
          }
        </div>

        <aside class="details-aside">
          <div class="aside-card">
            <h4 class="aside-title">Before you import</h4>
            @if (isVendorMode) {
              <ul class="aside-list">
                @for (note of vendorImportNotes; track note) {
                  <li>{{ note }}</li>
                }
              </ul>
            } @else {
              <ul class="aside-list">
                <li>First row must match the migration template headers</li>
                <li><code>first_name</code> and <code>last_name</code> are optional</li>
                <li>Rows with <code>license_plate</code> also create a vehicle</li>
                <li>
                  Matching customers (email, phone, or name) are updated with the latest row data
                </li>
              </ul>
            }
            @if (sourceMode === 'csv') {
              <button
                type="button"
                class="template-btn"
                (click)="onDownloadTemplate()"
                [disabled]="isDownloadingTemplate"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path
                    d="M12 4v10m0 0 4-4m-4 4-4-4M5 18h14"
                    stroke="currentColor"
                    stroke-width="1.6"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
                {{ isDownloadingTemplate ? 'Downloading…' : 'Download CSV template' }}
              </button>
            }
          </div>
        </aside>
      </div>
    </section>

    <div class="alert alert-warning" role="status">
      If a customer already exists for this car wash (same email, phone, or name), their record will
      be <strong>updated</strong> with the latest information from this import. New customers are
      inserted as usual.
    </div>

    @if (fieldError) {
      <div class="alert alert-error" role="alert">{{ fieldError }}</div>
    }

    <div class="import-actions">
      @if (activeJobId && !isJobRunning && jobStatus) {
        <button type="button" class="secondary-btn" (click)="onResetAfterJob()">
          Start another import
        </button>
      }
      <button type="button" class="primary-btn" (click)="onSubmit()" [disabled]="!canSubmit">
        @if (isSubmitting) {
          Starting…
        } @else if (isJobRunning) {
          @if (isVendorMode) {
            Sync running…
          } @else {
            Import running…
          }
        } @else if (isVendorMode) {
          Start sync
        } @else {
          Start import
        }
      </button>
    </div>

    @if (isVendorMode && !credentialStatus?.configured && !credentialStatusLoading) {
      <p class="submit-blocked-hint">
        Save {{ activeVendorLabel }} credentials for this car wash before starting a sync.
      </p>
    } @else if (isVendorMode && isSyncPaused) {
      <p class="submit-blocked-hint">
        {{ activeVendorLabel }} sync is turned off for this car wash. Turn it on above to sync.
      </p>
    }

    @if (vendorJobId) {
      <section class="progress-panel" aria-live="polite">
        <div class="progress-panel-header">
          <div>
            <h3 class="progress-title">{{ activeVendorLabel }} sync</h3>
            <p class="progress-meta">Job {{ vendorJobId }} · {{ activeVendorLabel }}</p>
          </div>
          <span class="status-badge">{{ vendorJobStateLabel }}</span>
        </div>

        <!-- No percentage: the vendor report gives no reliable total until the
             pull finishes, and a fake bar would misrepresent progress. -->
        @if (isAwaitingFirstVendorPage) {
          <p class="vendor-waiting" role="status">{{ vendorWaitingMessage }}</p>
        }

        <dl class="vendor-stats">
          <div class="vendor-stat">
            <dt>Fetched</dt>
            <dd>
              {{ vendorJobProgress?.fetched ?? 0 }}
              @if (vendorReportTotal) {
                <span class="vendor-stat-of">of {{ vendorReportTotal }}</span>
              }
            </dd>
          </div>
          <div class="vendor-stat">
            <dt>Sent to import</dt>
            <dd>{{ vendorJobProgress?.rowsSent ?? 0 }}</dd>
          </div>
          <div class="vendor-stat">
            <dt>Imported</dt>
            <dd>{{ vendorJobProgress?.inserted ?? 0 }}</dd>
          </div>
          <div class="vendor-stat">
            <dt>Updated</dt>
            <dd>{{ vendorJobProgress?.updated ?? 0 }}</dd>
          </div>
          <div class="vendor-stat" [class.warn]="vendorSkippedCount > 0">
            <dt>Skipped</dt>
            <dd>{{ vendorSkippedCount }}</dd>
          </div>
        </dl>

        @if (vendorSkippedCount > 0) {
          <div class="alert alert-warning" role="status">{{ vendorSkippedMessage }}</div>
        }

        @if (vendorJobFailedReason) {
          <div class="alert alert-error">{{ vendorJobFailedReason }}</div>
        }
      </section>
    }

    @if (activeJobId) {
      <section class="progress-panel" aria-live="polite">
        <div class="progress-panel-header">
          <div>
            <h3 class="progress-title">Import progress</h3>
            <p class="progress-meta">
              Job {{ activeJobId }}
              @if (jobSourceType) {
                · {{ jobSourceType === 'csv' ? 'CSV' : 'Google Sheet' }}
              }
            </p>
          </div>
          <span class="status-badge" [attr.data-tone]="statusTone">{{ statusLabel }}</span>
        </div>

        <div class="progress-track-row">
          <div
            class="progress-bar"
            role="progressbar"
            [attr.aria-valuenow]="progressPercent"
            aria-valuemin="0"
            aria-valuemax="100"
          >
            <div
              class="progress-bar-fill"
              [class.success]="jobStatus === 'completed'"
              [class.danger]="jobStatus === 'failed'"
              [style.width.%]="progressPercent"
            ></div>
          </div>
          <span class="progress-percent">{{ progressPercent }}%</span>
        </div>

        @if (jobFailedReason) {
          <div class="alert alert-error">{{ jobFailedReason }}</div>
        }
      </section>
    }

    <section class="history-panel">
      <div class="history-header">
        <h3 class="history-title">Recent imports</h3>
        <!-- Withheld in fileOnly (the migration tab): clearing deletes every
             completed/failed row for the company whatever its source, and the
             migration tab derives a system's stage from those rows. Wiping them
             makes a cut-over system read as never connected and drops it off the
             tab, so the button is not offered where it can do that silently. -->
        @if (history.length && !fileOnly) {
          <button
            type="button"
            class="secondary-btn clear-history-btn"
            (click)="onClearHistoryClick()"
            [disabled]="isClearingHistory || isJobRunning || historyLoading"
          >
            {{ isClearingHistory ? 'Clearing…' : 'Clear history' }}
          </button>
        }
      </div>

      @if (historyLoading) {
        <p class="history-empty">Loading history…</p>
      } @else if (!history.length) {
        <p class="history-empty">No import history yet for this car wash.</p>
      } @else {
        <div class="history-table-wrap">
          <table class="history-table">
            <thead>
              <tr>
                <th>Car wash</th>
                <th>Status</th>
                <th>Actor</th>
                <th>Source</th>
                <th>Original file</th>
                <th>Inserted</th>
                <th>Updated</th>
                <th>Processed</th>
                <th>Links</th>
                <th>Started</th>
              </tr>
            </thead>
            <tbody>
              @for (item of history; track item.id) {
                <tr>
                  <td>{{ item.company_name || '—' }}</td>
                  <td>
                    <span class="status-badge">{{ getHistoryStatusLabel(item.status) }}</span>
                  </td>
                  <td>{{ item.actor_name || '—' }}</td>
                  <td>{{ getHistorySourceLabel(item) }}</td>
                  <td>
                    @if (item.source_file_url) {
                      <a
                        class="source-file-link"
                        [href]="item.source_file_url"
                        target="_blank"
                        rel="noopener noreferrer"
                        [attr.download]="
                          item.source_type === 'csv'
                            ? item.source_file_name || 'migration.csv'
                            : null
                        "
                      >
                        {{
                          item.source_file_name ||
                            (item.source_type === 'google_sheet' ? 'Open sheet' : 'Download')
                        }}
                      </a>
                    } @else {
                      —
                    }
                  </td>
                  <td>{{ item.inserted_count ?? 0 }}</td>
                  <td>{{ item.duplicate_count ?? 0 }}</td>
                  <td>{{ item.processed_count ?? 0 }}</td>
                  <td>
                    @if (
                      item.result_invalid_s3_url ||
                      item.result_duplicate_s3_url ||
                      item.result_failed_s3_url
                    ) {
                      <div class="history-links">
                        @if (item.result_invalid_s3_url) {
                          <a
                            [href]="item.result_invalid_s3_url"
                            target="_blank"
                            rel="noopener noreferrer"
                            >Invalid</a
                          >
                        }
                        @if (item.result_duplicate_s3_url) {
                          <a
                            [href]="item.result_duplicate_s3_url"
                            target="_blank"
                            rel="noopener noreferrer"
                            >Updated</a
                          >
                        }
                        @if (item.result_failed_s3_url) {
                          <a
                            [href]="item.result_failed_s3_url"
                            target="_blank"
                            rel="noopener noreferrer"
                            >Failed</a
                          >
                        }
                      </div>
                    } @else {
                      —
                    }
                  </td>
                  <td>{{ formatHistoryDate(item.created_at) }}</td>
                </tr>
              }
            </tbody>
          </table>
        </div>

        @if (historyTotalPages > 1) {
          <div class="history-pager">
            <span class="history-range">{{ historyRangeLabel }}</span>
            <div class="history-pager-controls">
              <button
                type="button"
                class="secondary-btn"
                (click)="goToHistoryPage(historyPage - 1)"
                [disabled]="!canGoPrevHistoryPage"
              >
                Previous
              </button>
              <span class="history-page-label">
                Page {{ historyPage }} of {{ historyTotalPages }}
              </span>
              <button
                type="button"
                class="secondary-btn"
                (click)="goToHistoryPage(historyPage + 1)"
                [disabled]="!canGoNextHistoryPage"
              >
                Next
              </button>
            </div>
          </div>
        }
      }
    </section>
  </div>
</div>

<app-confirmation-modal
  [isVisible]="showClearHistoryConfirmation"
  [data]="clearHistoryConfirmationData"
  (cancel)="onCancelClearHistory()"
  (confirm)="onConfirmClearHistory()"
>
</app-confirmation-modal>
